import React, { useEffect, useMemo, useState } from 'react';
import { Button, Combobox, Input, Select, Switch } from '../../../ui/components';
import { useT } from '../../../i18n';
import { useCommercialSettings } from '../../../app/PropertyContext';

const EMPTY = {
  mode: 'existing',
  itemId: '',
  newName: '',
  newSku: '',
  newCategoryId: '',
  newStockUomId: '',
  supplierSku: '',
  description: '',
  purchaseUomId: '',
  packSize: 1,
  priceAmount: '',
  priceCurrency: 'USD',
  minimumOrderQuantity: '',
  leadTimeDays: '',
  isPreferred: false,
  isActive: true,
};

function fromLink(l, defaultCurrency) {
  if (!l) return { ...EMPTY, priceCurrency: defaultCurrency || 'USD' };
  return {
    mode: 'existing',
    itemId: l.itemId ?? '',
    newName: '', newSku: '', newCategoryId: '', newStockUomId: '',
    supplierSku: l.supplierSku ?? '',
    description: l.description ?? '',
    purchaseUomId: l.purchaseUomId ?? '',
    packSize: l.packSize ?? 1,
    priceAmount: l.price?.amount ?? '',
    priceCurrency: l.price?.currency ?? defaultCurrency ?? 'USD',
    minimumOrderQuantity: l.minimumOrderQuantity ?? '',
    leadTimeDays: l.leadTimeDays ?? '',
    isPreferred: l.isPreferred ?? false,
    isActive: l.isActive ?? true,
  };
}

export default function SupplierCatalogItemForm({
  link, items = [], units = [], categories = [], defaultCurrency = 'USD',
  linkedItemIds = [], submitting, onSubmit, onCancel,
  onItemSearch, itemsSearching = false,
}) {
  const editing = !!link;
  const t = useT();
  const { currencies } = useCommercialSettings();
  const [form, setForm] = useState(() => fromLink(link, defaultCurrency));
  const [errors, setErrors] = useState({});

  useEffect(() => { setForm(fromLink(link, defaultCurrency)); setErrors({}); }, [link, defaultCurrency]);

  const currencyOptions = useMemo(() => {
    const codes = [...currencies];
    if (form.priceCurrency && !codes.includes(form.priceCurrency)) codes.push(form.priceCurrency);
    return codes.map((c) => ({ value: c, label: c }));
  }, [currencies, form.priceCurrency]);

  // Only items not already on this supplier's price list are linkable.
  const itemOptions = useMemo(
    () => items
      .filter((i) => !linkedItemIds.includes(i.id))
      .map((i) => ({ value: i.id, label: i.name, description: i.sku || undefined })),
    [items, linkedItemIds],
  );
  const unitOptions = useMemo(
    () => [{ value: '', label: t('— No unit —') }, ...units.map((u) => ({ value: u.id, label: `${u.name} (${u.code})` }))],
    [units, t],
  );
  const categoryOptions = useMemo(
    () => [{ value: '', label: t('— No category —') }, ...categories.map((c) => ({ value: c.id, label: c.name }))],
    [categories, t],
  );

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const next = {};
    if (!editing) {
      if (form.mode === 'existing' && !form.itemId) next.itemId = t('Choose an item.');
      if (form.mode === 'new' && !form.newName.trim()) next.newName = t('Enter an item name.');
    }
    if (!(Number(form.packSize) > 0)) next.packSize = t('Pack size must be greater than 0.');
    if (form.priceAmount !== '' && Number(form.priceAmount) < 0) next.priceAmount = t('Must be ≥ 0.');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const priceLine = {
      supplierSku: form.supplierSku.trim() || null,
      description: form.description.trim() || null,
      purchaseUomId: form.purchaseUomId || null,
      packSize: Number(form.packSize) || 1,
      price: form.priceAmount === '' ? null : { amount: Number(form.priceAmount), currency: (form.priceCurrency || defaultCurrency || 'USD').toUpperCase() },
      minimumOrderQuantity: form.minimumOrderQuantity === '' ? null : Number(form.minimumOrderQuantity),
      leadTimeDays: form.leadTimeDays === '' ? null : Number(form.leadTimeDays),
      isPreferred: form.isPreferred,
      isActive: form.isActive,
    };

    if (editing) {
      onSubmit({ kind: 'update', payload: priceLine });
      return;
    }
    if (form.mode === 'new') {
      onSubmit({
        kind: 'add',
        payload: {
          ...priceLine,
          newItem: {
            name: form.newName.trim(),
            sku: form.newSku.trim() || null,
            categoryId: form.newCategoryId || null,
            stockUomId: form.newStockUomId || null,
          },
        },
      });
    } else {
      onSubmit({ kind: 'add', payload: { ...priceLine, itemId: form.itemId } });
    }
  };

  const linkedItemName = editing ? items.find((i) => i.id === link.itemId)?.name || link.itemId : null;

  return (
    <form className="pp-form" onSubmit={handleSubmit}>
      {editing ? (
        <div className="ps-field">
          <span className="ps-field__label">{t('Item')}</span>
          <div className="sp-catalog__fixed-item">{linkedItemName}</div>
        </div>
      ) : (
        <>
          <div className="sp-catalog__mode">
            <button type="button" className={`sp-catalog__mode-btn ${form.mode === 'existing' ? 'is-active' : ''}`} onClick={() => setForm((f) => ({ ...f, mode: 'existing' }))}>{t('Existing item')}</button>
            <button type="button" className={`sp-catalog__mode-btn ${form.mode === 'new' ? 'is-active' : ''}`} onClick={() => setForm((f) => ({ ...f, mode: 'new' }))}>{t('Create new item')}</button>
          </div>

          {form.mode === 'existing' ? (
            <div className="ps-field">
              <span className="ps-field__label">{t('Catalog item')}</span>
              <Combobox
                options={itemOptions}
                value={form.itemId}
                onChange={(v) => setForm((f) => ({ ...f, itemId: v }))}
                onSearch={onItemSearch}
                placeholder={t('Search catalog…')}
                searchPlaceholder={t('Search name or SKU…')}
                emptyText={itemsSearching ? t('Searching…') : t('No items available')}
                ariaLabel={t('Catalog item')}
              />
              {errors.itemId && <span className="ps-field__error">{errors.itemId}</span>}
            </div>
          ) : (
            <>
              <div className="pp-form__row">
                <Input label={t('Item name')} placeholder={t('e.g. Roma tomatoes')} value={form.newName} onChange={set('newName')} error={errors.newName} />
                <Input label="SKU" placeholder={t('Internal code (optional)')} value={form.newSku} onChange={set('newSku')} />
              </div>
              <div className="pp-form__row">
                <div className="ps-field">
                  <span className="ps-field__label">{t('Category')}</span>
                  <Select value={form.newCategoryId} onChange={(v) => setForm((f) => ({ ...f, newCategoryId: v }))} options={categoryOptions} ariaLabel={t('Category')} searchable />
                </div>
                <div className="ps-field">
                  <span className="ps-field__label">{t('Stock unit')}</span>
                  <Select value={form.newStockUomId} onChange={(v) => setForm((f) => ({ ...f, newStockUomId: v }))} options={unitOptions} ariaLabel={t('Stock unit')} searchable />
                </div>
              </div>
            </>
          )}
        </>
      )}

      <div className="pp-form__row">
        <Input label={t('Supplier SKU')} placeholder={t('Vendor reference')} value={form.supplierSku} onChange={set('supplierSku')} />
        <Input label={t('Description')} placeholder={t('As listed by supplier')} value={form.description} onChange={set('description')} />
      </div>

      <div className="pp-form__row">
        <div className="ps-field">
          <span className="ps-field__label">{t('Purchase unit')}</span>
          <Select value={form.purchaseUomId} onChange={(v) => setForm((f) => ({ ...f, purchaseUomId: v }))} options={unitOptions} ariaLabel={t('Purchase unit')} searchable />
        </div>
        <Input label={t('Pack size')} type="number" hint={t('Stock units per purchase unit (e.g. 24).')} value={form.packSize} onChange={set('packSize')} error={errors.packSize} />
      </div>

      <div className="pp-form__row">
        <Input label={t('Price (per purchase unit)')} type="number" placeholder="0.00" value={form.priceAmount} onChange={set('priceAmount')} error={errors.priceAmount} />
        <div className="ps-field">
          <span className="ps-field__label">{t('Currency')}</span>
          <Select value={form.priceCurrency} onChange={(v) => setForm((f) => ({ ...f, priceCurrency: v }))} options={currencyOptions} ariaLabel={t('Currency')} />
        </div>
      </div>

      <div className="pp-form__row">
        <Input label={t('Min. order qty')} type="number" value={form.minimumOrderQuantity} onChange={set('minimumOrderQuantity')} />
        <Input label={t('Lead time (days)')} type="number" value={form.leadTimeDays} onChange={set('leadTimeDays')} />
      </div>

      <Switch checked={form.isPreferred} onChange={(v) => setForm((f) => ({ ...f, isPreferred: v }))} label={t('Preferred source for this item')} />
      <Switch checked={form.isActive} onChange={(v) => setForm((f) => ({ ...f, isActive: v }))} label={t('Active')} />

      <div className="pp-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>{t('Cancel')}</Button>
        <Button type="submit" variant="primary" loading={submitting}>{editing ? t('Save changes') : t('Add to price list')}</Button>
      </div>
    </form>
  );
}
